
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateCurrency, useUpdateCurrency, Currency } from '@/hooks/useCurrencies';

interface CurrencyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currency?: Currency | null;
}

export function CurrencyDialog({ open, onOpenChange, currency }: CurrencyDialogProps) {
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    iso_code: '',
    price_margin: 0,
    rate_to_sar: 1,
    rate_from_sar: 1,
  });

  const createCurrency = useCreateCurrency();
  const updateCurrency = useUpdateCurrency();

  const isEditing = !!currency;

  useEffect(() => {
    if (currency) {
      setFormData({
        name_ar: currency.name_ar,
        name_en: currency.name_en,
        iso_code: currency.iso_code,
        price_margin: currency.price_margin,
        rate_to_sar: currency.rate_to_sar,
        rate_from_sar: currency.rate_from_sar,
      });
    } else {
      setFormData({
        name_ar: '',
        name_en: '',
        iso_code: '',
        price_margin: 0,
        rate_to_sar: 1,
        rate_from_sar: 1,
      });
    }
  }, [currency, open]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // تحديث تلقائي للأسعار المتبادلة
    if (field === 'rate_to_sar' && typeof value === 'number' && value > 0) {
      setFormData(prev => ({
        ...prev,
        rate_from_sar: 1 / value
      }));
    } else if (field === 'rate_from_sar' && typeof value === 'number' && value > 0) {
      setFormData(prev => ({
        ...prev,
        rate_to_sar: 1 / value
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEditing && currency) {
      updateCurrency.mutate(
        { 
          id: currency.id, 
          ...formData,
          last_updated: new Date().toISOString()
        },
        {
          onSuccess: () => {
            onOpenChange(false);
          }
        }
      );
    } else {
      createCurrency.mutate(formData, {
        onSuccess: () => {
          onOpenChange(false);
        }
      });
    }
  };

  const isLoading = createCurrency.isPending || updateCurrency.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'تعديل العملة' : 'إضافة عملة جديدة'}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'قم بتعديل بيانات العملة أدناه'
              : 'أدخل بيانات العملة الجديدة'
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name_ar">الاسم بالعربية *</Label>
              <Input
                id="name_ar"
                value={formData.name_ar}
                onChange={(e) => handleInputChange('name_ar', e.target.value)}
                placeholder="مثال: الدولار الأمريكي"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name_en">الاسم بالإنجليزية *</Label>
              <Input
                id="name_en"
                value={formData.name_en}
                onChange={(e) => handleInputChange('name_en', e.target.value)}
                placeholder="Example: US Dollar"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="iso_code">كود ISO *</Label>
              <Input
                id="iso_code"
                value={formData.iso_code}
                onChange={(e) => handleInputChange('iso_code', e.target.value.toUpperCase())}
                placeholder="USD"
                maxLength={3}
                className="uppercase"
                required
                disabled={isEditing && currency?.iso_code === 'SAR'}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="price_margin">هامش السعر (%)</Label>
              <Input
                id="price_margin"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.price_margin}
                onChange={(e) => handleInputChange('price_margin', parseFloat(e.target.value) || 0)}
                placeholder="2.50"
              />
              <p className="text-xs text-gray-500">هامش الربح المضاف على سعر الصرف</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rate_to_sar">سعر التحويل إلى ريال سعودي *</Label>
              <Input
                id="rate_to_sar"
                type="number"
                step="0.0001"
                min="0.0001"
                value={formData.rate_to_sar}
                onChange={(e) => handleInputChange('rate_to_sar', parseFloat(e.target.value) || 1)}
                placeholder="3.7500"
                required
                disabled={isEditing && currency?.iso_code === 'SAR'}
              />
              <p className="text-xs text-gray-500">كم ريال يساوي وحدة واحدة من هذه العملة</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rate_from_sar">سعر التحويل من ريال سعودي *</Label>
              <Input
                id="rate_from_sar"
                type="number"
                step="0.0001"
                min="0.0001"
                value={formData.rate_from_sar}
                onChange={(e) => handleInputChange('rate_from_sar', parseFloat(e.target.value) || 1)}
                placeholder="0.2667"
                required
                disabled={isEditing && currency?.iso_code === 'SAR'}
              />
              <p className="text-xs text-gray-500">كم وحدة من هذه العملة يساوي ريال واحد</p>
            </div>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm text-blue-800 font-medium">معاينة التحويل:</p>
            <p className="text-xs text-blue-600 mt-1">
              1 {formData.name_ar || 'عملة'} = {formData.rate_to_sar.toFixed(4)} ريال سعودي
            </p>
            <p className="text-xs text-blue-600">
              1 ريال سعودي = {formData.rate_from_sar.toFixed(4)} {formData.name_ar || 'عملة'}
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'جاري الحفظ...' : (isEditing ? 'تحديث' : 'إضافة')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
